import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { SelectButton } from "primereact/selectbutton";
import { Tag } from "primereact/tag";

import {
  Checkbox,
  Dropdown,
  EmptyState,
  ErrorState,
  ForbiddenState,
  HubTable,
  Icon,
  LoadingState,
} from "@/ds";
import { hubTableCartao, HubTableCartao } from "@/shared/components/hub-table-cartao";
import type { OrdenarEscoposPadraoPor } from "@/modules/financeiro-configuracoes";
import {
  ESCOPOS_PADRAO_ACOES_KEY,
  ESCOPOS_PADRAO_ACOES_WIDTH_REM,
  ESCOPOS_PADRAO_DESCRICAO,
  ESCOPOS_PADRAO_FORBIDDEN_MESSAGE,
  ESCOPOS_PADRAO_LOADING_LABEL,
  ESCOPOS_PADRAO_MOSTRAR_INATIVOS_LABEL,
  ESCOPOS_PADRAO_NOVA_VERSAO_LABEL,
  ESCOPOS_PADRAO_NOVO_LABEL,
  ESCOPOS_PADRAO_ROWS_PER_PAGE_LABEL,
  ESCOPOS_PADRAO_SEM_VERSAO_DESCRICAO,
  ESCOPOS_PADRAO_SEM_VERSAO_TITULO,
  ESCOPOS_PADRAO_TAG_INATIVO,
  ESCOPOS_PADRAO_TITULO,
  ESCOPOS_PADRAO_VERSAO_ROTULO,
  ESCOPOS_PADRAO_VISOES_LABEL,
} from "./escopos-padrao.constants";
import type { EscoposPadraoColumnKey } from "./escopos-padrao.constants";
import { EscopoPadraoDialog } from "./components/escopo-padrao-dialog";
import { InativarAtivarEscopoPadrao } from "./components/inativar-ativar-escopo-padrao";
import { NovaVersaoDialog } from "./components/nova-versao-dialog";
import styles from "./escopos-padrao.module.scss";
import type { EscopoPadraoTabelaLinha, EscoposPadraoViewModel } from "./types-escopos-padrao";

const INATIVOS_INPUT_ID = "escopos-padrao-mostrar-inativos";

type ItemDaVisao = { context: { option: { testId: string } } };

function renderAcoes(linha: EscopoPadraoTabelaLinha) {
  return (
    <span className={styles.acoes}>
      {linha.acoes?.map((acao) => (
        <Button
          key={acao.tipo}
          type="button"
          className={hubTableCartao.actionButtonClassName}
          icon={<Icon name={acao.tipo === "editar" ? "pencil" : "power-off"} size={14} />}
          aria-label={acao.label}
          aria-haspopup="dialog"
          disabled={acao.desabilitada}
          onClick={acao.executar}
          data-testid={acao.testId}
          severity="secondary"
          text
        />
      ))}
    </span>
  );
}

export function EscoposPadraoView({
  conteudo,
  mensagem,
  recarregarVersoes,
  versao,
  visao,
  visoes,
  selecionarVisao,
  filtros,
  mostrarInativos,
  alternarInativos,
  novoEscopo,
  novaVersao,
  tabela,
  dialogo,
  dialogoNovaVersao,
  dialogoSituacao,
  avisoSomenteLeitura,
}: EscoposPadraoViewModel) {
  if (conteudo === "oculto") return null;

  return (
    <section
      className={styles.tela}
      aria-label={ESCOPOS_PADRAO_TITULO}
      data-testid="financeiro-escopos-padrao-screen"
    >
      <header className={styles.cabecalho}>
        <div className={styles.secao}>
          <h2 className={styles.titulo}>{ESCOPOS_PADRAO_TITULO}</h2>
          <p className={styles.descricao}>{ESCOPOS_PADRAO_DESCRICAO}</p>
        </div>
        {conteudo === "lista" ? (
          <SelectButton
            className={styles.visoes}
            value={visao}
            options={visoes}
            optionLabel="rotulo"
            optionValue="valor"
            allowEmpty={false}
            onChange={(evento) => selecionarVisao(evento.value)}
            aria-label={ESCOPOS_PADRAO_VISOES_LABEL}
            pt={{
              button: (opcoes) => ({
                "data-testid": (opcoes as unknown as ItemDaVisao).context.option.testId,
              }),
            }}
          />
        ) : null}
      </header>

      {conteudo === "carregando" ? (
        <LoadingState label={ESCOPOS_PADRAO_LOADING_LABEL} testId="escopos-padrao-loading" />
      ) : null}
      {conteudo === "sem-permissao" ? (
        <ForbiddenState
          description={mensagem ?? ESCOPOS_PADRAO_FORBIDDEN_MESSAGE}
          testId="escopos-padrao-sem-permissao"
        />
      ) : null}
      {conteudo === "erro" ? (
        <ErrorState
          message={mensagem ?? undefined}
          onRetry={recarregarVersoes}
          testId="escopos-padrao-erro"
        />
      ) : null}

      {conteudo === "sem-versao" || conteudo === "lista" ? (
        <div className={styles.barra}>
          <Dropdown
            className={styles.filtro}
            size="sm"
            value={versao.value}
            options={versao.options}
            optionLabel="label"
            optionValue="value"
            ariaLabel={ESCOPOS_PADRAO_VERSAO_ROTULO}
            showClear={false}
            disabled={versao.options.length === 0}
            valueTemplate={(opcao) => `${ESCOPOS_PADRAO_VERSAO_ROTULO}: ${opcao?.label ?? ""}`}
            onChange={(evento) => versao.onChange(evento.value)}
            data-testid="escopos-padrao-versao"
          />
          <div className={styles.botoes}>
            {novaVersao ? (
              <span
                title={novaVersao.dica ?? undefined}
                data-testid="escopos-padrao-nova-versao-dica"
              >
                <Button
                  type="button"
                  outlined
                  icon={<Icon name="plus" size={14} />}
                  label={ESCOPOS_PADRAO_NOVA_VERSAO_LABEL}
                  disabled={novaVersao.desabilitado}
                  onClick={novaVersao.executar}
                  data-testid="escopos-padrao-nova-versao"
                />
              </span>
            ) : null}
            {novoEscopo ? (
              <span title={novoEscopo.dica ?? undefined} data-testid="escopos-padrao-novo-dica">
                <Button
                  type="button"
                  icon={<Icon name="plus" size={14} />}
                  label={ESCOPOS_PADRAO_NOVO_LABEL}
                  disabled={novoEscopo.desabilitado}
                  onClick={novoEscopo.executar}
                  data-testid="escopos-padrao-novo"
                />
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      {avisoSomenteLeitura && conteudo === "lista" ? (
        <Message
          severity="info"
          className={`p-message-simple p-message-block ${styles.aviso}`}
          text={avisoSomenteLeitura}
          data-testid="escopos-padrao-somente-leitura"
        />
      ) : null}

      {conteudo === "sem-versao" ? (
        <EmptyState.Root size="sm" testId="escopos-padrao-sem-versao">
          <EmptyState.Illustration name="sem-dados" />
          <EmptyState.Title>{ESCOPOS_PADRAO_SEM_VERSAO_TITULO}</EmptyState.Title>
          <EmptyState.Description>{ESCOPOS_PADRAO_SEM_VERSAO_DESCRICAO}</EmptyState.Description>
        </EmptyState.Root>
      ) : null}

      {conteudo === "lista" ? (
        <>
          <div className={styles.barra}>
            <div className={styles.filtros}>
              {filtros.map((filtro) => (
                <Dropdown
                  key={filtro.testId}
                  className={styles.filtro}
                  size="sm"
                  value={filtro.value}
                  options={filtro.options}
                  optionLabel="label"
                  optionValue="value"
                  ariaLabel={filtro.rotulo}
                  showClear={false}
                  valueTemplate={(opcao) => `${filtro.rotulo}: ${opcao?.label ?? ""}`}
                  onChange={(evento) => filtro.onChange(evento.value)}
                  data-testid={filtro.testId}
                />
              ))}
            </div>
            <div className={styles.inativos}>
              <Checkbox
                inputId={INATIVOS_INPUT_ID}
                checked={mostrarInativos}
                onChange={alternarInativos}
                data-testid="escopos-padrao-mostrar-inativos"
              />
              <label htmlFor={INATIVOS_INPUT_ID}>{ESCOPOS_PADRAO_MOSTRAR_INATIVOS_LABEL}</label>
            </div>
          </div>

          <HubTableCartao<EscopoPadraoTabelaLinha, EscoposPadraoColumnKey, OrdenarEscoposPadraoPor>
            value={tabela.items}
            totalRecords={tabela.total}
            table={tabela.table}
            settings={tabela.settings}
            status={tabela.status}
            errorMessage={tabela.errorMessage}
            loadingLabel={ESCOPOS_PADRAO_LOADING_LABEL}
            emptyNoun="escopo"
            emptyGender="m"
            className={styles.tabela}
            hasFilters={tabela.hasFilters}
            hasSearch={false}
            onClear={tabela.onClear}
            onRetry={tabela.reload}
            rowClassName={(linha) => (linha.inativa ? styles.linhaInativa : undefined)}
            dataTableProps={tabela.dataTableProps}
            rowsPerPageLabel={ESCOPOS_PADRAO_ROWS_PER_PAGE_LABEL}
            testId="escopos-padrao"
            data-testid="escopos-padrao-table-wrapper"
            data-first-load={tabela.isFirstLoad || undefined}
          >
            {tabela.colunas.map((coluna, indice) => (
              <HubTable.Column<EscopoPadraoTabelaLinha, EscoposPadraoColumnKey>
                key={coluna.key}
                field={coluna.key}
                columnKey={coluna.key}
                header={coluna.header}
                sortable={coluna.ordenavel}
                align={coluna.align}
                alignHeader={coluna.align}
                frozen={indice < tabela.colunasFixas}
                headerClassName={indice === 0 ? styles.colunaPrimeira : undefined}
                bodyClassName={indice === 0 ? styles.colunaPrimeira : undefined}
                style={
                  coluna.widthRem
                    ? { width: `${coluna.widthRem}rem`, minWidth: `${coluna.widthRem}rem` }
                    : undefined
                }
                body={(linha) =>
                  indice === 0 ? (
                    <span className={styles.primeira}>
                      <span className={styles.primeiraTitulo}>
                        <span className={styles.nome}>{linha.celulas[coluna.key]}</span>
                        {linha.inativa ? (
                          <Tag value={ESCOPOS_PADRAO_TAG_INATIVO} severity="secondary" />
                        ) : null}
                      </span>
                      <span className={styles.apoio}>{linha.celulas.ultimaAlteracao}</span>
                    </span>
                  ) : (
                    linha.celulas[coluna.key]
                  )
                }
              />
            ))}
            {tabela.mostrarAcoes ? (
              <HubTable.Column<EscopoPadraoTabelaLinha, EscoposPadraoColumnKey>
                field={ESCOPOS_PADRAO_ACOES_KEY}
                columnKey={ESCOPOS_PADRAO_ACOES_KEY}
                header=""
                align="center"
                frozen={tabela.acoesFixas}
                alignFrozen="right"
                bodyClassName={hubTableCartao.actionCellClassName}
                headerClassName={hubTableCartao.actionCellClassName}
                style={{
                  width: `${ESCOPOS_PADRAO_ACOES_WIDTH_REM}rem`,
                  minWidth: `${ESCOPOS_PADRAO_ACOES_WIDTH_REM}rem`,
                }}
                body={renderAcoes}
              />
            ) : null}
          </HubTableCartao>
        </>
      ) : null}
      {dialogo ? <EscopoPadraoDialog {...dialogo} /> : null}
      {dialogoSituacao ? <InativarAtivarEscopoPadrao {...dialogoSituacao} /> : null}
      {dialogoNovaVersao ? <NovaVersaoDialog {...dialogoNovaVersao} /> : null}
    </section>
  );
}
